import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { stopPropagation, isPortaledEvent, handleEscape, PREVENT_EVENT_CAPTURE, addListener } from '../../utils/event.js';
import { noop } from '../../utils/fn.js';
import { mergeProps } from '../../utils/merge-props.js';
import { preventTouchMove } from '../../utils/touch.js';
import { fromClientXY, ZERO, subtract } from '../../utils/vector.js';
import { GESTURE_START_ACTIONS } from './use-drag.utils.js';
import { base, dragging, draggingBody } from './use-drag.css.js';

const ACTIONS = {
    wait: () => ({ type: 'wait' }),
    cancel: () => ({ type: 'cancel' }),
    start: (origin) => ({ type: 'exit', origin }),
};
const IDLE_GESTURE = { phase: 0 /* Phase.IDLE */ };
/**
 * General-purpose drag hook, and the successor to `useDrag`. Handles pointer capture, dead zone
 * (optional), body class, touchmove preventDefault, and Escape-to-cancel. It intentionally does
 * NOT handle text-input ancestors, `data-no-dead-zone`, cancel-on-text-selection, or iOS
 * magnifying-glass timeouts — reach for `useContainerDrag` when the drag gesture has to coexist
 * with selectable text, editable inputs, or other child gestures.
 *
 * The lifecycle is: `onGestureStart` → `onDragStart` → `onDrag` (many) → final `onDrag` with
 * `commit: true` → `onDragEnd`. `onGestureStart` owns the gesture state at pointerdown;
 * `onDragStart` may transform it when the dead zone activates. Cancellations flow through a final
 * `onDrag` when activation occurred, then every accepted gesture ends through `onDragEnd` with an
 * explicit outcome.
 *
 * Escape is handled in capture phase with `stopPropagation`, so an in-progress drag wins the
 * "what does Escape do right now" hierarchy over enclosing menus, modals, etc.
 *
 * @see {@link https://fpl.figma.design/hooks/drag-v2}
 */
function useDragV2(ctx) {
    const gestureRef = useRef(IDLE_GESTURE);
    const dragEndRef = useRef(noop);
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    // Render-only state so consumers can read `dragging`. Use the gesture ref internally.
    const [dragging$1, setDragging] = useState(false);
    const dragProps = useMemo(() => {
        function registerAbortSignal(signal) {
            const gesture = gestureRef.current;
            if (gesture.phase !== 2 /* Phase.DRAGGING */)
                return;
            gesture.cleanups.push(addListener(signal, 'abort', () => {
                if (gestureRef.current === gesture)
                    dragEnd(null, 'cancel', true);
            }));
        }
        function deltaFromStart(e, gesture) {
            return subtract(fromClientXY(e), gesture.start);
        }
        function fireDrag(gesture, e, delta, commit, cancelled) {
            const event = Object.assign(e, { delta, commit, cancelled });
            ctxRef.current.onDrag?.({ event, updateStart: gesture.updateStart }, gesture.state);
        }
        function dragStart(e) {
            const gesture = gestureRef.current;
            if (gesture.phase !== 1 /* Phase.PENDING */)
                return;
            gesture.phase = 2 /* Phase.DRAGGING */;
            setDragging(true);
            gesture.lastEvent = e;
            // Using pointerCapture means we don't have to attach window-level listeners; all events
            // from here on will be dispatched to this element.
            gesture.captureEl.setPointerCapture(e.pointerId);
            gesture.captureEl.setAttribute('data-pointer-capture', '');
            document.body.classList.add(draggingBody);
            // Once a drag starts, suppress `pointercancel` (touch/pen viewport reclaim) by preventing
            // the default touchmove. This can't be done via a returned event handler because it needs
            // to be `passive: false`.
            if (e.pointerType !== 'mouse') {
                gesture.cleanups.push(preventTouchMove());
            }
            if (ctxRef.current.onDragStart) {
                const nextState = ctxRef.current.onDragStart({ event: e, registerAbortSignal }, gesture.state);
                if (gestureRef.current !== gesture)
                    return;
                if (nextState !== undefined)
                    gesture.state = nextState;
            }
            // Fire once synchronously so callers can treat the first frame like any other. With a dead
            // zone this will have a non-zero delta (we'll have just crossed the threshold).
            fireDrag(gesture, e, deltaFromStart(e, gesture), false, false);
        }
        // Called with `e: null` only from the abort signal path. Kept loose in the signature because
        // encoding it with overloads doesn't help TypeScript narrow the body.
        function dragEnd(e, reason, cancelled) {
            const gesture = gestureRef.current;
            if (gesture.phase === 0 /* Phase.IDLE */ || (e && e.pointerId !== gesture.pointerId))
                return;
            // Detach the session before cleanup and callbacks so reentrant or stale events cannot end
            // it twice or accidentally end a gesture that a callback starts.
            gestureRef.current = IDLE_GESTURE;
            const finalEvent = getFinalEvent(e, gesture);
            const activated = gesture.phase === 2 /* Phase.DRAGGING */;
            setDragging(false);
            gesture.captureEl.removeAttribute('data-pointer-capture');
            gesture.cleanups.forEach((cleanup) => cleanup());
            document.body.classList.remove(draggingBody);
            if (activated && !cancelled)
                window.getSelection()?.empty();
            if (activated) {
                // On cancel, zero delta lets additive callers roll back to `start` by construction. On
                // AbortSignal/Escape we don't have a fresh event, so reuse the last one we saw; the caller
                // should read `e.cancelled` rather than the event's coords.
                const delta = cancelled ? ZERO : deltaFromStart(finalEvent, gesture);
                fireDrag(gesture, finalEvent, delta, true, cancelled);
            }
            const endEvent = Object.assign(finalEvent, { activated, cancelled, reason });
            ctxRef.current.onDragEnd?.({ event: endEvent }, gesture.state);
        }
        // Returns true if the caller should return early (pointer is still in the dead zone or a
        // decision was made), false if the caller should continue as a normal drag update.
        function checkAndHandleDeadZone(e, gesture) {
            if (!ctxRef.current.deadZone || gesture.phase !== 1 /* Phase.PENDING */)
                return false;
            // Defensive: if pointermove fires with no buttons pressed, we missed pointerup somehow.
            // Cancel quietly regardless of what the callback would have decided.
            if (e.buttons === 0) {
                dragEnd(e, 'cancel', true);
                return true;
            }
            const decision = resolveDeadZone(e, gesture, ctxRef.current.deadZone);
            switch (decision.type) {
                case 'wait':
                    break;
                case 'cancel':
                    dragEnd(e, 'cancel', true);
                    break;
                case 'exit':
                    // Start the drag. The drag's origin is the dead-zone exit point (the current pointer
                    // position) by default; the consumer can override via `actions.start(origin)` to snap
                    // to a boundary (e.g. the threshold in a radial dead zone).
                    gesture.start = decision.origin ?? fromClientXY(e);
                    dragStart(e);
                    break;
            }
            return true;
        }
        // dragEnd closes over the same refs the handlers do, so any instance is valid; expose the
        // current one for the unmount effect below.
        dragEndRef.current = dragEnd;
        return {
            onPointerDown(e) {
                if (isPortaledEvent(e) ||
                    // Ignore secondary pointerdown events on touch devices before we've seen pointerup.
                    gestureRef.current.phase !== 0 /* Phase.IDLE */ ||
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                    (!ctxRef.current.customButtonCheck && e.button !== 0 /* MouseButton.LEFT */) ||
                    // Temporary fix for old scrubbable inputs. We can't stopPropagation on their pointerdown
                    // because they rely on a non-capturing document-level listener.
                    e.target.hasAttribute('data-temporary-fpl-no-drag')) {
                    return;
                }
                const result = ctxRef.current.onGestureStart?.({
                    event: e,
                    actions: GESTURE_START_ACTIONS,
                }) ?? GESTURE_START_ACTIONS.accept();
                if (result.type === 'cancel')
                    return;
                e.stopPropagation();
                // Store the capture target and Escape cleanup as soon as the gesture is accepted. Both
                // are needed if the gesture ends before its dead zone activates the drag.
                const gesture = {
                    phase: 1 /* Phase.PENDING */,
                    start: fromClientXY(e),
                    updateStart(fn) {
                        gesture.start = fn(gesture.start);
                    },
                    pointerId: e.pointerId,
                    captureEl: result.captureTarget ?? e.currentTarget,
                    state: result.state,
                    lastEvent: e,
                    cleanups: [],
                };
                gestureRef.current = gesture;
                gesture.cleanups.push(handleEscape(() => dragEnd(null, 'escape', true)));
                if (ctxRef.current.deadZone && !result.skipDeadZone)
                    return;
                // Clear any selection at drag start so it doesn't trail the cursor or reappear at end.
                window.getSelection()?.empty();
                dragStart(e);
            },
            onPointerMove(e) {
                const gesture = gestureRef.current;
                if (gesture.phase === 0 /* Phase.IDLE */ || e.pointerId !== gesture.pointerId)
                    return;
                gesture.lastEvent = e;
                if (checkAndHandleDeadZone(e, gesture))
                    return;
                fireDrag(gesture, e, deltaFromStart(e, gesture), false, false);
            },
            // If the pointer leaves the element while waiting to cross the dead zone, start the drag
            // at the exit point. It feels better than cancelling.
            onPointerLeave(e) {
                const gesture = gestureRef.current;
                if (gesture.phase === 0 /* Phase.IDLE */ || e.pointerId !== gesture.pointerId)
                    return;
                gesture.lastEvent = e;
                checkAndHandleDeadZone(e, gesture);
            },
            onPointerUp(e) {
                dragEnd(e, 'pointerup', false);
            },
            // Chrome sometimes skips pointerup but always fires lostpointercapture. Safari sometimes
            // skips clientX/Y on lostpointercapture but always fires pointerup. Listening to both
            // means we get a reliable final delta on every browser.
            // See https://bugs.chromium.org/p/chromium/issues/detail?id=1515939
            onLostPointerCapture(e) {
                // Touch pointer capture is implicit, so `setPointerCapture` above implicitly cancels
                // the browser's capture to install ours. Ignore the resulting lostpointercapture; we'll
                // still get a pointerup when the finger lifts.
                if (e.pointerType === 'touch')
                    return;
                // Pointer-lock transitions can fire lostpointercapture spuriously during a drag.
                if (document.pointerLockElement === e.target)
                    return;
                dragEnd(e, 'pointerup', gestureRef.current.phase !== 2 /* Phase.DRAGGING */);
            },
            onPointerCancel(e) {
                dragEnd(e, 'cancel', true);
            },
            // TODO: remove once all draggable modals use the fpl version. Until then, stop propagation
            // so that dragging a child of an old draggable modal doesn't drag the modal itself.
            onMouseDown: stopPropagation,
        };
    }, []);
    const getDragProps = useCallback((...props) => mergeProps(dragProps, { className: clsx(base, PREVENT_EVENT_CAPTURE, { [dragging]: dragging$1 }) }, ...props), [dragProps, dragging$1]);
    // If the host unmounts mid-gesture, cancel the drag so we don't leave document.body stuck in
    // the dragging state (pointer-events: none) or leak the Escape/touch listeners.
    useEffect(() => () => {
        if (gestureRef.current.phase !== 0 /* Phase.IDLE */) {
            dragEndRef.current(null, 'cancel', true);
        }
    }, []);
    // Becoming disabled mid-gesture takes the release handlers away with it, so the pointerup that
    // would have ended the drag never reaches us — leaving the same stuck body and leaked listeners
    // as an unmount would. End it here instead, as a cancel, so the caller rolls back.
    useEffect(() => {
        if (ctx.disabled && gestureRef.current.phase !== 0 /* Phase.IDLE */) {
            dragEndRef.current(null, 'cancel', true);
        }
    }, [ctx.disabled]);
    if (ctx.disabled)
        return [false, mergeProps];
    return [dragging$1, getDragProps];
}
function getFinalEvent(e, gesture) {
    const finalEvent = e ?? gesture.lastEvent;
    // React clears `currentTarget` on a synthetic event after the original dispatch finishes
    // (see `executeDispatch` in react-dom). On Escape/AbortSignal we fire a final event from
    // outside a normal dispatch, so we patch `currentTarget` back to the captured element so
    // callers can use `e.currentTarget.style…` in their final `onDrag`/`onDragEnd`.
    if (!finalEvent.currentTarget) {
        finalEvent.currentTarget = gesture.captureEl;
    }
    return finalEvent;
}
function resolveDeadZone(e, gesture, deadZone) {
    const origin = gesture.start;
    const event = e;
    const decision = deadZone({ event, origin, actions: ACTIONS }, gesture.state);
    // pointerleave is the last event we'll deliver — pointermove won't fire once the pointer is
    // outside the captured element. `wait` here would hang the pre-drag forever, so promote it
    // to a default `start()` (begin the drag at the current pointer position). Consumers that
    // genuinely want to abort on leave should return `actions.cancel()`; consumers that want a
    // custom exit origin (e.g. an expanded-rect exit point) should return `actions.start(...)`.
    return e.type === 'pointerleave' && decision.type === 'wait' ? ACTIONS.start() : decision;
}

export { useDragV2 };
//# sourceMappingURL=use-drag-v2.js.map
